import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, TrendingUp, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CCY = [
    { code: "USD", symbol: "$", flag: "🇺🇸" },
    { code: "GBP", symbol: "£", flag: "🇬🇧" },
    { code: "CAD", symbol: "C$", flag: "🇨🇦" },
];

const fmtINR = (v) =>
    "₹" +
    Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const fmtRate = (v) => Number(v).toFixed(4);

export default function FxCalculator() {
    const [currency, setCurrency] = useState("USD");
    const [amount, setAmount] = useState(10000);
    const [result, setResult] = useState(null);
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const active = CCY.find((c) => c.code === currency);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/fx/rates`)
            .then((r) => {
                if (mounted) setRates(r.data || []);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    const calculate = async () => {
        setErr("");
        const value = Number(amount);
        if (!value || value <= 0) {
            setErr("Enter an amount greater than 0");
            return;
        }
        setLoading(true);
        try {
            const r = await axios.post(`${API}/fx/calculate`, {
                currency,
                amount: value,
            });
            setResult(r.data);
        } catch (e) {
            setErr("Could not calculate right now. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Auto-calc on mount + on currency change with current amount
    useEffect(() => {
        calculate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    const activeRate = useMemo(
        () => rates.find((r) => r.currency === currency),
        [rates, currency]
    );

    return (
        <section
            id="calculator"
            data-testid="fx-calculator-section"
            className="relative -mt-16 md:-mt-24 z-20 pb-20 md:pb-28 px-4 md:px-8"
        >
            <div className="mx-auto max-w-6xl">
                <div className="rounded-[28px] border border-zinc-200 bg-white shadow-[0_30px_120px_-40px_rgba(6,78,59,0.45)] overflow-hidden">
                    <div className="grid lg:grid-cols-2">
                        {/* Left: Inputs */}
                        <div className="p-6 sm:p-10 lg:p-12 bg-gradient-to-br from-white via-zinc-50 to-emerald-50/30">
                            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-bold text-emerald-700">
                                <TrendingUp className="h-3.5 w-3.5" />
                                Live FX Transparency
                            </span>
                            <h2
                                data-testid="calculator-headline"
                                className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-950"
                            >
                                See exactly what you'll save vs your bank.
                            </h2>
                            <p className="mt-3 text-zinc-600 text-base max-w-md">
                                Real interbank FX pricing. No hidden spreads.
                            </p>

                            <div className="mt-8">
                                <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Sending
                                </label>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    {CCY.map((c) => (
                                        <button
                                            key={c.code}
                                            type="button"
                                            data-testid={`fx-currency-${c.code}`}
                                            onClick={() => setCurrency(c.code)}
                                            className={`h-12 rounded-2xl border text-sm font-bold transition-all ${
                                                currency === c.code
                                                    ? "border-emerald-600 bg-emerald-600 text-white shadow-md"
                                                    : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-400"
                                            }`}
                                        >
                                            <span className="mr-1.5">{c.flag}</span>
                                            {c.code}/INR
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-6">
                                <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Amount
                                </label>
                                <div className="mt-2 relative">
                                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-zinc-400 font-mono">
                                        {active?.symbol}
                                    </span>
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min="1"
                                        step="100"
                                        data-testid="fx-amount-input"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        className="h-16 w-full rounded-2xl border border-zinc-200 bg-white pl-12 pr-4 font-mono text-2xl sm:text-3xl font-bold text-zinc-950 focus:border-emerald-500 focus:outline-none transition-colors"
                                    />
                                </div>
                            </div>

                            {err && (
                                <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
                                    <AlertCircle className="h-4 w-4" />
                                    {err}
                                </div>
                            )}

                            <button
                                type="button"
                                data-testid="fx-calculate-btn"
                                onClick={calculate}
                                disabled={loading}
                                className="mt-8 inline-flex h-14 w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-zinc-950 px-8 text-base font-bold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60"
                            >
                                {loading ? "Calculating…" : "Calculate My Savings"}
                                <ArrowRight className="h-4 w-4" />
                            </button>

                            {activeRate && (
                                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-zinc-500 font-mono">
                                    <span>Interbank: {fmtRate(activeRate.interbank_rate)}</span>
                                    <span>· TruoPay: {fmtRate(activeRate.truopay_rate)}</span>
                                    <span>· Bank avg: {fmtRate(activeRate.bank_rate)}</span>
                                </div>
                            )}
                        </div>

                        {/* Right: Output */}
                        <div className="relative bg-gradient-to-br from-[#04110D] via-[#062a20] to-[#04110D] text-white p-6 sm:p-10 lg:p-12 overflow-hidden">
                            <div className="absolute inset-0 bg-grid-emerald opacity-50" aria-hidden />
                            <div
                                aria-hidden
                                className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl"
                            />
                            <div className="relative">
                                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-emerald-300">
                                    Recipient receives
                                </div>

                                <div className="mt-6 space-y-4">
                                    {/* Bank row */}
                                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm text-zinc-400">Traditional Bank</div>
                                            <div className="text-[10px] font-bold tracking-widest uppercase text-amber-300/90">
                                                ~{activeRate?.bank_markup_pct ?? 2.5}% markup
                                            </div>
                                        </div>
                                        <div className="mt-2 font-mono text-2xl sm:text-3xl font-bold text-zinc-300 line-through decoration-amber-400/60 decoration-2">
                                            {result ? fmtINR(result.bank_inr) : "—"}
                                        </div>
                                    </div>

                                    {/* TruoPay row */}
                                    <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5 glow-emerald">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm text-emerald-200">TruoPay</div>
                                            <div className="text-[10px] font-bold tracking-widest uppercase text-emerald-300">
                                                {activeRate?.truopay_markup_pct ?? 0.3}% fee
                                            </div>
                                        </div>
                                        <div
                                            data-testid="fx-truopay-amount"
                                            className="mt-2 font-mono text-3xl sm:text-4xl font-extrabold text-white"
                                        >
                                            {result ? fmtINR(result.truopay_inr) : "—"}
                                        </div>
                                    </div>

                                    {/* Savings */}
                                    <div className="rounded-2xl border border-amber-300/40 bg-gradient-to-r from-amber-300/20 to-transparent p-5">
                                        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-amber-300">
                                            You save
                                        </div>
                                        <div className="mt-1 flex items-baseline gap-3">
                                            <div
                                                data-testid="fx-savings-amount"
                                                className="font-mono text-3xl sm:text-4xl font-extrabold text-amber-200"
                                            >
                                                {result ? fmtINR(result.savings_inr) : "—"}
                                            </div>
                                            {result && (
                                                <span className="text-sm font-bold text-amber-300/90">
                                                    (+{result.savings_pct}%)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <p className="mt-6 text-xs text-zinc-400 leading-relaxed">
                                    Real interbank FX pricing. No hidden spreads. Rates shown are
                                    indicative and update in real time on the live platform.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
